import { Middleware } from '@reduxjs/toolkit';
import { normalizeCanvas } from '../../utils/canvasSnapshot';
import { historyPush } from './workflowSlice';

/**
 * 캔버스 언두/리두 기록 미들웨어 — 캔버스를 바꾸는 액션이 지나가기 "직전" 상태를 past 스택에 쌓는다.
 * 액션 전·후의 normalizeCanvas 시그니처를 비교해 실제로 문서가 달라졌을 때만 기록하므로,
 * 클릭 선택(setNodes 의 selected 변경)이나 무변화 액션은 항목을 만들지 않는다.
 *
 * 두 가지 잡음을 추가로 걸러야 실사용이 된다:
 *  1) 캔버스 로드: 워크플로를 열면 addNode/addEdge 가 수십 번 쏟아진다 — 로드는 편집이 아니다.
 *     탐색기·마법사·버전복구 모두 reset→setCurrentWorkflow→(주입)→requestFitView 순서라,
 *     resetWorkflow 에서 기록을 멈추고 requestFitView 에서 다시 켠다.
 *     **멈춤에는 시한을 둔다.** 켜는 신호(requestFitView)가 오지 않는 경로가 있어서다 —
 *     앱이 뜰 때·프로젝트를 고를 때는 캔버스를 싣지 않고 reset/포인터 설정만 하는데, 예전에는
 *     그때 멈춘 기록이 그 세션 내내 켜지지 않아 **언두 버튼이 계속 꺼져 있었다**(실측).
 *     로드 주입은 밀리초 단위로 몰아치므로 시한을 넘길 일이 없고, 넘겼다면 그건 로드가 아니다.
 *  2) 연속 편집: 드래그(setNodes 가 매 프레임)·속성 타이핑(updateNodeData 가 키 입력마다)은
 *     한 번의 편집으로 묶는다 — 같은 액션 타입이 짧은 간격으로 이어지면 첫 번째 것만 남긴다.
 *     (첫 프레임이 편집 "이전" 상태라, 언두가 정확히 드래그 전으로 돌아간다)
 */

/** 기록 대상 — 캔버스 문서(노드/엣지/설정)를 바꾸는 액션들 */
const RECORDED = new Set([
  'workflow/addNode',
  'workflow/addFragment',
  'workflow/replaceCanvas',
  'workflow/addEdge',
  'workflow/removeNode',
  'workflow/removeEdge',
  'workflow/updateNode',
  'workflow/updateEdge',
  'workflow/reconnectEdge',
  'workflow/updateNodeData',
  'workflow/updateNodePosition',
  'workflow/setNodes',
  'workflow/setWorkflowSettings',
]);

/** 짧은 간격으로 이어지면 한 편집으로 묶는 액션 (드래그·타이핑·설정 다이얼로그) */
const COALESCED = new Set([
  'workflow/setNodes',
  'workflow/updateNodePosition',
  'workflow/updateNodeData',
  'workflow/setWorkflowSettings',
]);
const COALESCE_MS = 900;

/**
 * 한 번의 삭제 — 서브플로(그룹)를 지우면 React Flow 가 연결선들 → 그룹 → 안의 노드들을 **각각** remove 로 보낸다.
 * 따로 기록하면 되돌리기를 그 수만큼 눌러야 하고, 기록 한도를 넘은 앞쪽(먼저 지워진 연결선)은 영영 돌아오지 않았다
 * (현장 2026-10-12: 노드 8개가 든 서브플로 삭제 → 되돌리기 여러 번, 연결선은 복원 안 됨).
 * 같은 삭제에서 잇달아 오는 removeNode/removeEdge 는 첫 기록 하나에 묶는다 — 되돌리기 한 번에 삭제 전으로 돌아간다.
 */
const DELETES = new Set(['workflow/removeNode', 'workflow/removeEdge']);
const DELETE_BURST_MS = 250;

/** 로드 주입으로 볼 수 있는 최대 시간 — 이 시간이 지나면 멈춤이 저절로 풀린다 */
const LOAD_WINDOW_MS = 5000;

export const historyMiddleware: Middleware = (store) => {
  let suspendedUntil = 0;           // 캔버스 로드 중 (resetWorkflow → requestFitView, 최대 LOAD_WINDOW_MS)
  let lastType = '';
  let lastAt = 0;

  return (next) => (action: any) => {
    const type: string = action?.type || '';
    // 진단용 — 콘솔에서 window.__actLog = [] 를 만들어 두면 워크플로 액션 흐름이 쌓인다 (개발 편의)
    const probe = (window as any).__actLog;
    if (probe && type.startsWith('workflow/')) {
      const w0 = store.getState().workflow;
      probe.push(`${type} pre=${w0.nodes.length} past=${w0.history.past.length}`);
    }

    if (type === 'workflow/resetWorkflow') {
      suspendedUntil = Date.now() + LOAD_WINDOW_MS;   // 이어질 주입(addNode…)은 편집이 아니다
      lastType = ''; lastAt = 0;
      return next(action);
    }
    if (type === 'workflow/requestFitView') {
      suspendedUntil = 0;           // 탐색기/마법사가 주입을 끝내고 화면을 맞출 때
      return next(action);
    }
    if (type === 'workflow/undo' || type === 'workflow/redo') {
      lastType = ''; lastAt = 0;    // 언두 직후의 편집은 새 항목으로
      return next(action);
    }

    if (Date.now() < suspendedUntil || !RECORDED.has(type)) return next(action);
    // 바로 앞 편집에 합친다 — 끌어 놓자마자 그룹 소속을 바꾸는 것처럼 한 동작의 뒷부분이라 되돌리기 한 번에 함께 돌아가야 할 때
    if (action?.meta?.historyMerge) return next(action);
    const w = store.getState().workflow;
    if (!w.current?.id) return next(action);

    const now = Date.now();
    const burst = (COALESCED.has(type) && type === lastType && now - lastAt < COALESCE_MS)
      || (DELETES.has(type) && DELETES.has(lastType) && now - lastAt < DELETE_BURST_MS);
    if (burst) {                        // 이어지는 드래그/타이핑 — 첫 기록에 묶인다
      lastAt = now;
      return next(action);
    }

    const preSig = normalizeCanvas(w.nodes, w.edges, w.settings);
    const pre = { nodes: w.nodes, edges: w.edges, settings: w.settings, sig: preSig };
    const result = next(action);
    const after = store.getState().workflow;
    if (normalizeCanvas(after.nodes, after.edges, after.settings) !== preSig) {
      store.dispatch(historyPush(pre));
      // **실제로 바뀐 편집만** 묶음을 시작한다. 예전에는 바뀐 것 없는 setNodes(노드를 누를 때의 선택 변경)도
      // 묶음을 열어, 바로 이어진 끌기가 그 묶음에 들어가 기록되지 않았다 — 노드 이동을 되돌릴 수 없었다(2026-10-01)
      lastType = type;
      lastAt = now;
    } else {
      lastType = '';
    }
    return result;
  };
};
